import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { CentroBeneficio, SolicitudesAltaService } from 'src/app/services/workflows/solicitudes-alta.service';
import { mensajeDeError } from 'src/app/services/workflows/workflows.service';

/**
 * Los centros de beneficio como un select de varios (Adrián, o24 del 13-oct: «colocar los centros de
 * beneficio en un combo, y que el combo sea configurable para poder agregar los que correspondan»).
 * Reemplaza las etiquetas que se tocaban una por una. El administrador de altas agrega un centro
 * nuevo desde el mismo desplegable (`POST /centros-beneficio`), y queda marcado.
 * El desplegable usa las clases `.kb-menu*` de styles.css, igual que `app-kb-menu`.
 */
@Component({
  selector: 'app-centros-select',
  template: `
    <div class="cs-ancla">
      <button type="button" class="kb-select cs-boton" [id]="inputId" aria-haspopup="listbox" [attr.aria-expanded]="abierto"
              [attr.aria-labelledby]="rotuloId" (click)="alternar($event)">
        <span class="cs-texto" [class.text-muted-foreground]="!seleccion.length">{{ resumen }}</span>
        <i class="fas fa-chevron-down kb-select-flecha" aria-hidden="true"></i>
      </button>
      <div class="kb-menu cs-menu" *ngIf="abierto" (click)="$event.stopPropagation()">
        <div role="listbox" aria-multiselectable="true" [attr.aria-labelledby]="rotuloId">
          <label class="kb-menu-item cs-opcion" role="option" *ngFor="let c of centros" [attr.aria-selected]="marcado(c.codigo)">
            <input type="checkbox" [checked]="marcado(c.codigo)" (change)="alternarCentro(c.codigo)">
            <span>{{ c.nombre }}</span>
          </label>
          <p class="cs-vacio" *ngIf="!centros.length">Todavía no hay centros de beneficio en el catálogo.</p>
        </div>
        <ng-container *ngIf="esAdmin">
          <div class="kb-menu-sep" role="separator"></div>
          <button type="button" class="kb-menu-item" *ngIf="!agregando" (click)="empezarAAgregar()">
            <i class="fas fa-plus" aria-hidden="true"></i> Agregar centro de beneficio
          </button>
          <div class="cs-agregar" *ngIf="agregando">
            <label class="kb-label" [for]="inputId + 'Nuevo'">Nombre del centro nuevo</label>
            <div class="cs-agregar-fila">
              <input [id]="inputId + 'Nuevo'" type="text" class="kb-input" maxlength="60" [(ngModel)]="nuevo"
                     (keydown.enter)="agregar()" (keydown.escape)="agregando = false" placeholder="Por ejemplo, Óptica">
              <button type="button" class="kb-btn kb-btn-primary kb-btn-sm" [disabled]="guardando || nuevo.trim().length < 3" (click)="agregar()">
                {{ guardando ? 'Agregando…' : 'Agregar' }}
              </button>
            </div>
            <p class="kb-hint kb-texto-peligro" *ngIf="error" role="alert">{{ error }}</p>
          </div>
        </ng-container>
      </div>
    </div>`,
  styles: [`
    .cs-ancla { position: relative; }
    .cs-boton { width: 100%; text-align: left; display: flex; align-items: center; cursor: pointer; }
    .cs-texto { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .cs-menu { left: 0; right: 0; min-width: 0; max-height: 20rem; overflow-y: auto; }
    .cs-opcion input { accent-color: hsl(var(--primary)); }
    .cs-vacio { padding: 7px 8px; font-size: 0.8125rem; color: hsl(var(--muted-foreground)); }
    .cs-agregar { padding: 6px 8px 8px; }
    .cs-agregar-fila { display: flex; gap: 0.5rem; align-items: center; }
  `],
})
export class CentrosSelectComponent {
  @Input() centros: CentroBeneficio[] = [];
  @Input() seleccion: string[] = [];
  @Input() esAdmin = false;
  @Input() inputId = 'centrosSelect';
  @Input() rotuloId = '';
  @Output() seleccionChange = new EventEmitter<string[]>();
  /** Un centro recién agregado al catálogo: el padre lo suma a su lista. */
  @Output() creado = new EventEmitter<CentroBeneficio>();

  abierto = false;
  agregando = false;
  nuevo = '';
  guardando = false;
  error = '';

  constructor(private el: ElementRef, private _sa: SolicitudesAltaService) { }

  /** «Electrodomésticos, Hogar y muebles» o «Elige uno o más». */
  get resumen(): string {
    if (!this.seleccion.length) return 'Elige uno o más';
    const nombres = this.seleccion.map((c) => this.centros.find((x) => x.codigo === c)?.nombre || c);
    return nombres.length > 2 ? `${nombres.slice(0, 2).join(', ')} y ${nombres.length - 2} más` : nombres.join(', ');
  }

  marcado(codigo: string): boolean { return this.seleccion.includes(codigo); }

  alternar(ev: Event): void {
    ev.stopPropagation();
    this.abierto = !this.abierto;
    if (!this.abierto) this.agregando = false;
  }

  alternarCentro(codigo: string): void {
    this.seleccionChange.emit(this.marcado(codigo) ? this.seleccion.filter((c) => c !== codigo) : [...this.seleccion, codigo]);
  }

  empezarAAgregar(): void { this.agregando = true; this.nuevo = ''; this.error = ''; }

  agregar = async (): Promise<void> => {
    if (this.guardando || this.nuevo.trim().length < 3) return;
    this.guardando = true; this.error = '';
    try {
      const c = await this._sa.crearCentroBeneficio(this.nuevo.trim());
      this.creado.emit(c);
      if (!this.marcado(c.codigo)) this.seleccionChange.emit([...this.seleccion, c.codigo]);
      this.agregando = false; this.nuevo = '';
    } catch (e: any) {
      this.error = mensajeDeError(e, 'No se pudo agregar el centro de beneficio.');
    } finally {
      this.guardando = false;
    }
  };

  @HostListener('document:click', ['$event'])
  cerrarAfuera(ev: Event): void {
    if (this.abierto && !this.el.nativeElement.contains(ev.target)) { this.abierto = false; this.agregando = false; }
  }

  @HostListener('document:keydown.escape')
  cerrarConEscape(): void { if (this.abierto && !this.agregando) this.abierto = false; }
}
